import { Alert, Typography } from "antd";
import dayjs from "dayjs";
import { useCallback, useMemo, useState } from "react";
import { useMe } from "@/modules/auth/hooks/useAuth";
import { PageHeader } from "@/shared/components/PageHeader";
import { useUrlParams } from "@/shared/hooks/useUrlState";
import { DashboardChart } from "../components/DashboardChart";
import { DashboardFilter } from "../components/DashboardFilter";
import { DashboardStats } from "../components/DashboardStats";
import { TopBrigadesCard } from "../components/TopBrigadesCard";
import { useBrigadeRatings, useDashboardStats } from "../hooks/useDashboard";

const { Text } = Typography;

/** Sentinel meaning "explicitly cleared" — absent param means "primary org". */
const ORG_NONE = "none";

export function DashboardPage() {
	const { data: meUser } = useMe();
	const { searchParams, setParams } = useUrlParams();

	const now = dayjs();
	const year = Number.parseInt(searchParams.get("year") ?? "", 10) || now.year();
	const month = Number.parseInt(searchParams.get("month") ?? "", 10) || now.month() + 1;

	const primaryOrg = meUser?.organizations?.find((o) => o.isPrimary) ?? meUser?.organizations?.[0];
	const orgParam = searchParams.get("org");
	const organizationId = orgParam === ORG_NONE ? null : (orgParam ?? primaryOrg?.id ?? null);

	// Label of a deep-tree org isn't recoverable from the URL alone; keep the
	// last picked label and fall back to the user's own org list.
	const [pickedName, setPickedName] = useState<string>("");
	const organizationName = useMemo(() => {
		if (!organizationId) {
			return "";
		}
		return (
			meUser?.organizations?.find((o) => o.id === organizationId)?.name ??
			(organizationId === primaryOrg?.id ? primaryOrg?.name : undefined) ??
			pickedName
		);
	}, [organizationId, meUser, primaryOrg, pickedName]);

	const {
		data: stats,
		isLoading,
		error,
	} = useDashboardStats({
		organizationId: organizationId || "",
		year,
		month,
	});
	const { data: brigadeRatings, isLoading: isLoadingBrigadeRatings } = useBrigadeRatings(
		organizationId || ""
	);

	const handleOrganizationChange = useCallback(
		(value: string | null, name?: string) => {
			setPickedName(name ?? "");
			setParams({ org: value ?? ORG_NONE });
		},
		[setParams]
	);

	const handleReset = () => {
		setPickedName("");
		setParams({ org: null, year: null, month: null });
	};

	return (
		<div className="p-2">
			<PageHeader
				level={2}
				title="Dashboard"
				subtitle={
					<>
						Tizimning umumiy holati va statistik ma'lumotlari
						{organizationName && <Text strong> ({organizationName})</Text>}
					</>
				}
			/>

			<DashboardFilter
				organizationId={organizationId}
				onOrganizationChange={handleOrganizationChange}
				onReset={handleReset}
			/>

			{error && (
				<Alert
					title="Xatolik yuz berdi"
					description="Dashboard ma'lumotlarini yuklashda xatolik yuz berdi. Iltimos, qaytadan urinib ko'ring."
					type="error"
					showIcon
					className="mb-6"
				/>
			)}

			{!(organizationId || isLoading) && organizationName === "" && (
				<Alert
					title="Tashkilot tanlanmagan"
					description="Statistikani ko'rish uchun tashkilotni tanlang."
					type="info"
					showIcon
					className="mb-6"
				/>
			)}

			<DashboardStats data={stats} loading={isLoading} />

			<div className="mt-4">
				<TopBrigadesCard brigades={brigadeRatings?.brigades} loading={isLoadingBrigadeRatings} />
			</div>

			<DashboardChart
				data={stats}
				loading={isLoading}
				year={year}
				month={month}
				onYearChange={(y) => setParams({ year: y === now.year() ? null : y })}
				onMonthChange={(m) => setParams({ month: m === now.month() + 1 ? null : m })}
			/>
		</div>
	);
}
